import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Text,
  Alert,
  Button,
  Group,
  Badge,
  Divider,
  UnstyledButton,
  Paper,
  ThemeIcon,
  LoadingOverlay,
  Center,
} from '@mantine/core';
import { IconPlus, IconCheck } from '@tabler/icons-react';
import {
  CodeableConcept,
  Patient,
  Specimen,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { getPatientOrPractitionerName } from '../utils/common-helpers';
import { SpecimenCreateEditDrawer } from '../specimens/SpecimenCreateEditDrawer';
import { SpecimenDisplayCard } from '../specimens/SpecimenDisplayCard';
import { SpecimenCreateEditForm } from '../specimens/SpecimenCreateEditForm';
import { formatCodeableConcept, formatHumanName } from '@medplum/core';
import { useLinkSpecimens } from '../hooks/useLinkSpecimens';
import { useSpecimenActions } from '../hooks/useSpecimenActions';
import { useSpecimenForm } from '../hooks/useSpecimenForm';
import { LoaderWithChildren } from '@/components/LoaderWithChildren';
import { OrderBanner } from './OrderBanner';
import { isEqual } from 'lodash';

interface SpecimenSelectionContentProps {
  opened: boolean;
  onClose: () => void;
  patient?: Patient;
  requiredSampleType: CodeableConcept;
  serviceRequest: ServiceRequest;
  linkedSpecimens?: Specimen[];
  onSuccess: () => Promise<void>;
}

export function SpecimenSelectionContent({
  opened,
  onClose,
  patient,
  requiredSampleType,
  serviceRequest,
  linkedSpecimens = [],
  onSuccess,
}: SpecimenSelectionContentProps): JSX.Element {
  const { t } = useTranslation(['lims', 'common']);
  const [specimens, setSpecimens] = useState<Specimen[]>([]);
  const [selectedSpecimens, setSelectedSpecimens] =
    useState<Specimen[]>(linkedSpecimens);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);

  const { loadSpecimens: loadSpecimensAction, loading } = useSpecimenActions();

  const linkedSpecimenIds = useMemo(
    () => linkedSpecimens.map((s) => s.id).join(','),
    [linkedSpecimens]
  );

  const handleCloseModal = () => {
    setSelectedSpecimens([]);
    onClose();
  };

  // Called after inline creation: trigger parent refresh then close
  const handleAfterCreate = useCallback(async () => {
    setSelectedSpecimens([]);
    await onSuccess().catch(console.error);
    onClose();
  }, [onSuccess, onClose]);

  // Inline create form — reuses the same hook + component as the full drawer
  const {
    form: createForm,
    loading: createLoading,
    selectedPatient,
    selectedOrders,
    availableOrders,
    loadingOrders,
    handleSubmit: handleCreateSubmit,
    handlePatientChange,
    handleOrdersChange,
  } = useSpecimenForm({
    patientId: patient?.id,
    defaultSampleType: requiredSampleType,
    opened,
    onClose: handleAfterCreate,
    serviceRequest,
  });

  const { linkSpecimens, linkingLoading } = useLinkSpecimens({
    serviceRequest,
    requiredTypeCode: requiredSampleType?.coding?.[0].code,
    onSuccess,
    onClose: handleCloseModal,
  });

  useEffect(() => {
    if (!opened || !patient) {
      setSpecimens([]);
      setSelectedSpecimens([]);
      return;
    }

    const fetchSpecimens = async () => {
      const loadedSpecimens = await loadSpecimensAction(
        patient,
        requiredSampleType
      );
      setSpecimens(loadedSpecimens);
    };

    fetchSpecimens();
  }, [
    opened,
    patient,
    requiredSampleType,
    linkedSpecimenIds,
    loadSpecimensAction,
  ]);

  const handleSpecimenCreated = () => {
    setSelectedSpecimens([]);
    setCreateEditModalOpen(false);
    onClose();
    onSuccess();
  };

  if (!patient || !requiredSampleType) {
    return (
      <Alert color="red">
        {!patient && t('specimensPage.noPatientFound', { ns: 'lims' })}
        {!requiredSampleType &&
          t('specimensPage.noSampleTypeDefined', { ns: 'lims' })}
      </Alert>
    );
  }

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={linkingLoading} />

      <OrderBanner
        serviceRequest={serviceRequest}
        patient={patient}
        specimenType={requiredSampleType}
      />

      <LoaderWithChildren isLoading={loading}>
        {specimens.length === 0 ? (
          <>
            <Paper withBorder bg="gray.0" radius="md">
              <Center py="xl">
                <Stack align="center" gap="md">
                  <Text size="30px" c="dimmed">
                    🔍
                  </Text>
                  <Stack align="center" gap="xs">
                    <Text fw={500} size="md" c="dimmed">
                      {t(
                        'specimenSelection.noSpecimensFound',
                        'No specimens found',
                        { ns: 'lims' }
                      )}
                    </Text>
                    <Text size="sm" c="dimmed" ta="center">
                      {t(
                        'specimenSelection.noSpecimensForType',
                        'No available specimens of type «{{type}}» for {{context}} {{patientName}}',
                        {
                          ns: 'lims',
                          type: formatCodeableConcept(requiredSampleType),
                          context:
                            linkedSpecimens.length > 0
                              ? t(
                                  'specimenSelection.forLinking',
                                  'linking to',
                                  { ns: 'lims' }
                                )
                              : t(
                                  'specimenSelection.forPatient',
                                  'the patient',
                                  { ns: 'lims' }
                                ),
                          patientName: formatHumanName(patient?.name?.[0]),
                        }
                      )}
                    </Text>
                  </Stack>
                </Stack>
              </Center>
            </Paper>

            <Paper bg="green.0" p="md" radius="md">
              <Stack>
                <Group>
                  <ThemeIcon bg="green.3" size="lg">
                    <IconPlus />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={500} c="dimmed">
                      {t(
                        'specimenSelection.createNewSpecimenTitle',
                        'Create New Specimen',
                        { ns: 'lims' }
                      )}
                    </Text>
                    <Text c="dimmed">
                      {t(
                        'specimenSelection.createNewSpecimenDescription',
                        'Enter a new specimen and it will be linked automatically',
                        { ns: 'lims' }
                      )}
                    </Text>
                  </Stack>
                </Group>

                <Divider />

                <SpecimenCreateEditForm
                  form={createForm}
                  patient={patient}
                  defaultSampleType={requiredSampleType}
                  selectedPatient={selectedPatient}
                  selectedOrders={selectedOrders}
                  availableOrders={availableOrders}
                  loadingOrders={loadingOrders}
                  onPatientChange={handlePatientChange}
                  onOrdersChange={handleOrdersChange}
                  hideOrderSelection
                />
              </Stack>
            </Paper>

            {/* Action Buttons */}
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                disabled={createLoading}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                onClick={() => createForm.onSubmit(handleCreateSubmit)()}
                loading={createLoading}
              >
                {t('specimenModal.create', {
                  ns: 'lims',
                  defaultValue: 'Δημιουργία',
                })}{' '}
                &{' '}
                {t('orderDetails.linkSpecimens', {
                  ns: 'lims',
                  defaultValue: 'Σύνδεση',
                })}
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Stack gap="sm">
              <Group gap="xs">
                <Text fw={500}>
                  {t('specimenSelection.title', {
                    patientName: getPatientOrPractitionerName(patient),
                  })}
                </Text>
                <Badge size="sm" variant="light">
                  {specimens.length}
                </Badge>
              </Group>
              <Text c="dimmed" mt={-14}>
                {t(
                  'specimenSelection.existingSpecimensSameType',
                  'Existing specimens of the patient with the same type',
                  { ns: 'lims' }
                )}
              </Text>

              {specimens.map((specimen) => {
                const isSelected = selectedSpecimens.some(
                  (s) => s.id === specimen.id
                );
                return (
                  <Group key={specimen.id}>
                    <UnstyledButton
                      flex={1}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedSpecimens(
                            selectedSpecimens.filter(
                              (s) => s.id !== specimen.id
                            )
                          );
                        } else {
                          setSelectedSpecimens([
                            ...selectedSpecimens,
                            specimen,
                          ]);
                        }
                      }}
                    >
                      <SpecimenDisplayCard
                        specimen={specimen}
                        patient={patient}
                        bg={isSelected ? 'green.0' : undefined}
                      />
                    </UnstyledButton>
                    {isSelected && <IconCheck color="green" />}
                  </Group>
                );
              })}
            </Stack>

            <Divider label={t('or', { ns: 'common' })} labelPosition="center" />

            <Button
              variant="light"
              onClick={() => setCreateEditModalOpen(true)}
              leftSection={<IconPlus size={16} />}
            >
              {t('specimensPage.createSpecimen', { ns: 'lims' })}
            </Button>

            {/* Action Buttons */}
            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={handleCloseModal}
                disabled={linkingLoading}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                onClick={() => linkSpecimens(selectedSpecimens)}
                disabled={
                  isEqual(selectedSpecimens, linkedSpecimens) || linkingLoading
                }
                loading={linkingLoading}
              >
                {t('orderDetails.linkSpecimens', { ns: 'lims' })}
              </Button>
            </Group>
          </>
        )}
      </LoaderWithChildren>

      {/* Create/Edit Specimen Modal */}
      <SpecimenCreateEditDrawer
        opened={createEditModalOpen}
        onClose={handleSpecimenCreated}
        patient={patient}
        defaultSampleType={requiredSampleType}
        serviceRequest={serviceRequest}
      />
    </Stack>
  );
}
